import React, { useContext, useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { SetNewPasswordError } from "iam-client/dist/client/password-reset/passwordReset";
import { OnNewPasswordRequiredApi } from "iam-client/dist/wrapper/resetPasswordFlow";
import { useMessageSource } from "react-message-source";
import PasswordField from "../../../pages/shared/components/formik/text-input/password";
import ApplicationPageContent from "../../../ui/layout/page/content/application";
import ContentFooter from "../../../ui/layout/page/content/footer";
import NavigationBar from "../../../ui/layout/page/content/footer/navigation-bar";
import FormContainer from "../../../ui/layout/page/forms-container/form-base-container";
import InputContainer from "../../../ui/layout/page/forms-container/form-item-container/input-container";
import TypographyItemContainer from "../../../ui/layout/page/typography-container/typography-item-container";
import TypographyContainer from "../../../ui/layout/page/typography-container/typography-item-container";
import CustomTypography from "../../../ui/widgets/typography";
import { displayErrorInField } from "../../../util/formik";
import { ErrorContext } from "../../error/provider";
import usePasswordResetNewPasswordFormValidationSchema from "./validator";
import { LanguageContext } from "../../../context/languageContext";

import logging from "../../../util/logging";
import { useNavigate } from "react-router-dom";
const log = logging("registration flow");

type FormData = {
  email: string;
  newPassword?: string;
};

export default function PasswordResetNewPasswordForm({
  onNext,
  errors,
  resetFormErrors,
}: {
  onNext: OnNewPasswordRequiredApi;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: any;
  resetFormErrors: () => void;
}): React.JSX.Element {
  const { language } = useContext(LanguageContext);
  const navigate = useNavigate();

  const { getMessage } = useMessageSource();

  const [formikActions, setFormikActions] =
    useState<FormikHelpers<FormData>>(null);

  const { setError: setGlobalError } = useContext(ErrorContext);

  const formErrors: SetNewPasswordError[] = errors;

  const next = (values: FormData, actions: FormikHelpers<FormData>) => {
    resetFormErrors();
    if (onNext) {
      onNext.proceed(values.newPassword);
    } else {
      log.error(
        "New password flow: Cannot proceed in new password form, due to a missing callback",
      );
    }
    setFormikActions(actions);
  };

  const previous = () => {
    navigate("/login");
  };

  if (formErrors) {
    if (formikActions) {
      let errorLookupText = "PasswordReset.ErrorOnAirlock";
      if (
        formErrors &&
        Array.isArray(formErrors) &&
        formErrors[0].code === "PASSWORD_POLICY_VIOLATED" &&
        formErrors[0].meta
      ) {
        errorLookupText =
          "PasswordReset." +
          formErrors[0].code +
          "." +
          formErrors[0].meta.detail;
      }
      formikActions.setFieldValue("newPassword", "", false);
      displayErrorInField(
        formikActions,
        "newPassword",
        getMessage(errorLookupText),
      );
    } else {
      setGlobalError(formErrors);
    }
  }

  return (
    <ApplicationPageContent>
      <TypographyContainer>
        <TypographyItemContainer>
          <CustomTypography variant="h3">
            {getMessage("PasswordReset.setNewPassword")}
          </CustomTypography>
          <CustomTypography variant="body1">
            {getMessage("PasswordReset.pleaseEnterANewPassword")}
          </CustomTypography>
        </TypographyItemContainer>
      </TypographyContainer>
      <Formik
        key={language}
        initialValues={{ email: "" }}
        validationSchema={usePasswordResetNewPasswordFormValidationSchema()}
        onSubmit={next}
      >
        {({ handleSubmit }) => {
          return (
            <Form>
              <FormContainer>
                <InputContainer>
                  <PasswordField
                    forceReRender
                    name="newPassword"
                    label={getMessage("Account.password")}
                    helperUnderInput={getMessage("Account.password-info")}
                  />
                </InputContainer>
              </FormContainer>
              <ContentFooter>
                <NavigationBar
                  backButtonText={getMessage("Onboarding.cancel")}
                  nextButtonText={getMessage("Onboarding.continue")}
                  onBack={previous}
                  onNext={handleSubmit}
                />
              </ContentFooter>
            </Form>
          );
        }}
      </Formik>
    </ApplicationPageContent>
  );
}
